
import { Vue, Component, Watch } from 'vue-property-decorator'
import Message from '@/types/message'
import Column from '@/types/column'
import TableSettings from '@/types/tableSettings'
import ActiveFilters from '@/components/listview/ActiveFilters.vue'
import Pagination from '@/types/pagination'
import axios from 'axios'
import PageHeader from '@/components/base/PageHeader.vue'
import { notification } from 'ant-design-vue'
import SearchableColumnFilter from '@/components/base/SearchableColumnFilter.vue'
import ls from '@/ls'
import { can } from '@/globalFunctions'

@Component({
  methods: { can },
  components: { SearchableColumnFilter, PageHeader, ActiveFilters }
})

export default class Messages extends Vue {
  settings = {
    routeNames: { create: 'messageCreate', show: 'messageShow', edit: 'messageEdit' },
    routeParamKey: 'messageId',
    permissionNames: { show: 'messages.view', edit: 'messages.edit', delete: 'messages.delete' },
    functionNames: { getList: 'getMessageList' },
    getterNames: { list: 'messageList', loading: 'loadingMessage' },
    localStorageName: 'messageTableSettings'
  }

  columns:Column[] = [
    {
      title: '#',
      key: 'id',
      dataIndex: 'id',
      filters: [],
      sorter: true,
      sortDirections: ['ascend', 'descend']
    },
    {
      title: 'Titel',
      key: 'title',
      dataIndex: 'title',
      filters: [],
      sorter: true,
      sortDirections: ['ascend', 'descend']
    },
    {
      title: 'Project',
      key: 'project.id',
      dataIndex: 'project',
      scopedSlots: { customRender: 'project', filterDropdown: 'customFilter' },
      onFilterDropdownVisibleChange: (visible:boolean):void => this.filterDropdownVisibleChange(visible),
      filters: [],
      sorter: false,
      sortDirections: ['ascend', 'descend']
    },
    {
      title: 'Bouwnummer',
      key: 'building.number',
      dataIndex: 'building',
      scopedSlots: { customRender: 'building' },
      filters: [],
      sorter: true,
      sortDirections: ['ascend', 'descend']
    },
    {
      title: 'Ingepland op',
      key: 'scheduled_at',
      dataIndex: 'scheduled_at',
      scopedSlots: { customRender: 'scheduled_at' },
      filters: [],
      sorter: true,
      sortDirections: ['ascend', 'descend']
    },
    {
      title: 'Gepubliceerd op',
      key: 'published_at',
      dataIndex: 'published_at',
      scopedSlots: { customRender: 'published_at' },
      filters: [],
      sorter: true,
      sortDirections: ['ascend', 'descend']
    },
    {
      title: 'Gelezen op',
      key: 'read_at',
      dataIndex: 'read_at',
      scopedSlots: { customRender: 'read_at' },
      filters: [],
      sorter: true,
      sortDirections: ['ascend', 'descend']
    },
    {
      title: 'Reactie mogelijk',
      key: 'can_respond',
      dataIndex: 'can_respond',
      scopedSlots: { customRender: 'can_respond' },
      filters: [],
      sorter: false,
      sortDirections: ['ascend', 'descend']
    },
    {
      title: 'Status',
      key: 'status',
      dataIndex: 'status',
      scopedSlots: { customRender: 'status' },
      filters: [
        {
          text: 'Ingepland',
          value: 1
        },
        {
          text: 'Gepubliceerd',
          value: 2
        }
      ],
      sorter: false,
      sortDirections: ['ascend', 'descend']
    },
    {
      title: 'Actie\'s',
      key: 'action',
      scopedSlots: { customRender: 'action' }
    }
  ]

  tableSettings:TableSettings = ls.get(this.settings.localStorageName) || {
    pagination: { current: 1, pageSize: 25 },
    showSizeChanger: true,
    pageSizeOptions: ['25', '50', '100'],
    activeFilters: null,
    sort: {
      columnKey: 'id',
      field: 'id',
      order: 'descend'
    },
    search: null
  }

  mounted () : void {
    this.reload()
    this.onTableSettingsChange()
    this.getProjectFilter()
  }

  reload () :void {
    this.$store.dispatch(this.settings.functionNames.getList, this.tableSettings)
  }

  getProjectFilter () : void {
    axios.post(process.env.VUE_APP_API_URL + '/filters/project/messages', { textColumn: 'name', valueColumn: 'id' }).then((res) => {
      this.columns[2].filters = res.data
    })
  }

  handleTableChange (pagination:{ current:number, pageSize:number }, filters:{[key: string]: number[]|string[]}, sort:{ columnKey:string, order:string }) : void {
    // Remove key from filters if filter is an empty object.
    if (filters && Object.keys(filters).length) {
      Object.keys(filters).forEach((key) => {
        if (!filters[key] || !filters[key].length) {
          delete filters[key]

          this.columns.forEach((column:Column) => {
            if (column.key === key) {
              column.filteredValue = null
            }
          })
        }
      })
    }

    // Set pagination, filters and sorting on tableSettings.
    this.tableSettings.pagination = pagination
    this.tableSettings.activeFilters = filters
    this.tableSettings.sort = sort.order ? sort : null

    // Get list based on filters and search term.
    this.reload()
  }

  handleSearchChange () : void {
    // Set page to 1 because of new search but retain filters and sorting.
    this.tableSettings.pagination.current = 1

    // Get list based on filters and search term.
    this.$store.dispatch(this.settings.functionNames.getList, this.tableSettings)
  }

  // Required for ActiveFilters component
  updateFilters (filters: {[key: string]: number[]|string[]|boolean[]}) : void {
    const { columns } = this

    columns.forEach((column:Column) => {
      column.filteredValue = column.key in filters ? filters[column.key] : []
    })

    this.tableSettings.activeFilters = Object.assign({}, filters)
    this.reload()
  }

  filterVisible = false
  filterDropdownVisibleChange (visible:boolean) : void {
    this.filterVisible = visible
  }

  onRowClick (rowData:{id:number}) : { on: { click: () => void }} {
    return {
      on: {
        click: () => {
          const keyboard = window.event as KeyboardEvent
          if (can(this.settings.permissionNames.show)) {
            const route = this.$router.resolve({ name: this.settings.routeNames.show, params: { [this.settings.routeParamKey]: rowData.id.toString() } })
            window.open(route.href, keyboard.ctrlKey ? '_blank' : '_self')
          }
        }
      }
    }
  }

  navigateTo (routeName:string, id:number|undefined) : void {
    if (id) {
      this.$router.push({ name: routeName, params: { [this.settings.routeParamKey]: id.toString() } })
    } else {
      this.$router.push({ name: routeName })
    }
  }

  previousPage () : void {
    this.$router.go(-1)
  }

  deleteMessage (id:number) : void {
    axios.delete(process.env.VUE_APP_API_URL + '/messages/' + id).then(() => {
      notification.success({ message: 'Bericht succesvol verwijderd!', description: 'Het bericht is succesvol verwijderd.', duration: 3 })
      this.$store.dispatch(this.settings.functionNames.getList, this.tableSettings)
    }).catch(() => {
      notification.error({ message: 'Fout tijdens verwijderen van bericht!', description: 'Er is iets mis gegaan. Probeer het later nog een keer.' })
    })
  }

  get itemList () : { data: Message[], meta: { per_page:string, total:number } } {
    return this.$store.getters[this.settings.getterNames.list]
  }

  get pagination () : Pagination {
    return {
      current: this.tableSettings.pagination.current,
      defaultPageSize: this.tableSettings.pagination.pageSize,
      pageSize: parseInt(this.itemList.meta.per_page),
      total: this.itemList.meta.total,
      showTotal: (total, range) => `Bericht ${range[0]} t/m ${range[1]} van de ${total}`,
      showSizeChanger: this.tableSettings.showSizeChanger,
      pageSizeOptions: this.tableSettings.pageSizeOptions
    }
  }

  get loading () : boolean {
    return this.$store.getters[this.settings.getterNames.loading].list
  }

  get navigationCollapsed () : boolean {
    return this.$store.getters.navigationCollapsed
  }

  get screenWidth () : number {
    return screen.width
  }

  @Watch('tableSettings', { deep: true })
  onTableSettingsChange () : void {
    // On tableSettings change, store them in local storage.
    ls.set(this.settings.localStorageName, this.tableSettings)

    axios.post(process.env.VUE_APP_API_URL + '/users/admin/settings', {
      settingName: this.settings.localStorageName,
      settingValue: this.tableSettings
    })

    // Set the current active filters on the columns.
    this.columns.forEach((column:Column) => {
      // Set active filters.
      if (this.tableSettings.activeFilters && column.key in this.tableSettings.activeFilters) {
        column.filteredValue = this.tableSettings.activeFilters[column.key]
      }

      // Set active sort order.
      if (this.tableSettings.sort && column.key === this.tableSettings.sort.columnKey) {
        column.defaultSortOrder = this.tableSettings.sort.order
        column.sortOrder = this.tableSettings.sort.order
      } else {
        column.sortOrder = false
      }
    })
  }
}
